import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { CalendarRange, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RANGE_OPTIONS = [
  { key: '7d',   label: 'Last 7 days' },
  { key: '30d',  label: 'Last 30 days' },
  { key: '90d',  label: 'Last 90 days' },
  { key: 'mtd',  label: 'This month' },
  { key: 'ytd',  label: 'This year' },
  { key: '365d', label: 'Last 12 months' },
];

/**
 * ServiceHistorySummary — iter-78 pt 10 (Master Architecture).
 *
 * Daily-bucketed service history feed for the calling user. Aggregates
 * trash + recycle pickups, violations reported, attendance points earned,
 * and hours logged. Searchable by Date, Day-of-week, Month, and Year.
 *
 * Renders inside the StaffDashboard "history" tab for every field-staff
 * role (agent / crew_leader / supervisor / manager / admin).
 */
export function ServiceHistorySummary({ user }) {
  const [range, setRange] = useState('30d');
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(
          `${BACKEND_URL}/api/service-history/me?range=${range}`,
          { credentials: 'include' },
        );
        if (!cancelled && r.ok) setPayload(await r.json());
      } catch (_) { /* silent */ }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [range]);

  const rows = (payload?.rows || []).filter((row) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    // Match against ISO date, weekday, month name, year.
    const d = new Date(row.date + 'T00:00:00Z');
    const weekday = d.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const month = d.toLocaleDateString('en-US', { month: 'long' }).toLowerCase();
    const year = String(d.getUTCFullYear());
    return row.date.includes(q) || weekday.includes(q) || month.includes(q) || year.includes(q);
  });

  const totals = payload?.totals || { trash: 0, recycle: 0, violations: 0, attendance_points: 0, hours: 0 };

  return (
    <div className="space-y-4" data-testid="service-history-summary">
      <Card className="bg-zinc-900 border-2 border-[#C9A962]/50 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#C9A962] font-black mb-1">
              Service History
            </p>
            <h2
              className="text-xl font-black text-white"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              data-testid="service-history-title"
            >
              {user?.name}&rsquo;s daily job log
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <CalendarRange className="h-4 w-4 text-zinc-400" />
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="h-9 px-2 rounded-md border border-zinc-700 bg-zinc-950 text-sm text-white"
              data-testid="service-history-range"
            >
              {RANGE_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="relative mb-3">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Search by date, day (Monday), month (August), or year (2026)…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full h-9 pl-8 pr-3 rounded-md border border-zinc-700 bg-zinc-950 text-sm text-white placeholder:text-zinc-500"
            data-testid="service-history-search"
          />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4" data-testid="service-history-totals">
          <TotalTile label="Trash" value={totals.trash} tone="emerald" />
          <TotalTile label="Recycle" value={totals.recycle} tone="blue" />
          <TotalTile label="Violations" value={totals.violations} tone="red" />
          <TotalTile label="Att. pts" value={totals.attendance_points} tone="orange" />
          <TotalTile label="Hours" value={(totals.hours || 0).toFixed(1)} tone="zinc" />
        </div>

        {loading ? (
          <p className="text-sm text-zinc-500 italic">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-zinc-500 italic">No service days match this filter.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="service-history-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-zinc-400 border-b border-zinc-800">
                  <th className="text-left py-2 pr-3">Date</th>
                  <th className="text-left py-2 pr-3">Day</th>
                  <th className="text-right py-2 pr-3">Trash</th>
                  <th className="text-right py-2 pr-3">Recycle</th>
                  <th className="text-right py-2 pr-3">Viol.</th>
                  <th className="text-right py-2 pr-3">Att. pts</th>
                  <th className="text-right py-2">Hours</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const d = new Date(row.date + 'T00:00:00Z');
                  const day = d.toLocaleDateString('en-US', { weekday: 'short' });
                  return (
                    <tr
                      key={row.date}
                      className="border-b border-zinc-900 hover:bg-zinc-800/50"
                      data-testid={`service-history-row-${row.date}`}
                    >
                      <td className="py-2 pr-3 font-mono text-zinc-300">{row.date}</td>
                      <td className="py-2 pr-3 text-zinc-400">{day}</td>
                      <td className="py-2 pr-3 text-right text-emerald-400 font-bold">{row.trash}</td>
                      <td className="py-2 pr-3 text-right text-blue-400 font-bold">{row.recycle}</td>
                      <td className="py-2 pr-3 text-right text-red-400">{row.violations}</td>
                      <td className="py-2 pr-3 text-right text-orange-300">{row.attendance_points}</td>
                      <td className="py-2 text-right text-zinc-300">{(row.hours || 0).toFixed(1)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

const TONE_MAP = {
  emerald: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10',
  blue:    'text-blue-300 border-blue-500/40 bg-blue-500/10',
  red:     'text-red-300 border-red-500/40 bg-red-500/10',
  orange:  'text-orange-300 border-orange-500/40 bg-orange-500/10',
  zinc:    'text-zinc-200 border-zinc-500/40 bg-zinc-500/10',
};

function TotalTile({ label, value, tone }) {
  return (
    <div className={`rounded-md border p-3 text-center ${TONE_MAP[tone]}`}>
      <p className="text-2xl font-black">{value}</p>
      <p className="text-[10px] uppercase tracking-wider mt-1">{label}</p>
    </div>
  );
}

export default ServiceHistorySummary;
